import { Link } from 'react-router-dom'
import {Sparkles, Shield, Wrench, Users, UserCheck, Droplets, Layers, HardHat, Hammer, ArrowRight, CheckCircle2} from 'lucide-react'
import SectionHeading from '@/components/SectionHeading'
import { services } from '@/data/services'

const iconMap: Record<string, any> = {
  Sparkles, Shield, Wrench, Users, UserCheck, Droplets, Layers, HardHat, Hammer,
}

function Services() {
  return (
    <div>
      {/* HERO */}
      <section className="bg-navy text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://static.lumi.new/6d/6d412fbffbd3e4bca84a065e725f59c8.png"
            alt="High-rise window cleaning service"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-navy/60" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-px bg-orange-accent" />
              <span className="text-xs uppercase tracking-[0.3em] text-orange-accent font-semibold">Services</span>
            </div>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              Full-service facility care, <span className="text-orange-accent">under one roof.</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/80 leading-relaxed">
              Eight core service lines, delivered by one accountable team. Consolidate vendors, simplify operations, elevate standards.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="space-y-20">
            {services.map((service, index) => {
              const Icon = iconMap[service.icon] || Sparkles
              const isReversed = index % 2 === 1
              return (
                <div
                  key={service.id}
                  id={service.id}
                  className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${isReversed ? 'lg:[&>*:first-child]:order-2' : ''}`}
                >
                  <div className="lg:col-span-5">
                    <div className="sticky top-28">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-14 h-14 bg-navy rounded-xl flex items-center justify-center">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="text-xs uppercase tracking-[0.2em] text-orange-accent font-semibold">
                          0{index + 1} / 0{services.length}
                        </div>
                      </div>
                      <h3 className="font-display text-3xl md:text-4xl font-bold text-navy leading-tight">{service.title}</h3>
                      <p className="mt-3 text-orange-accent font-medium italic">{service.tagline}</p>
                      <p className="mt-6 text-muted leading-relaxed">{service.description}</p>
                    </div>
                  </div>

                  <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="bg-grey-soft rounded-2xl p-7">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-orange-accent font-bold mb-5">Key Benefits</h4>
                      <ul className="space-y-3">
                        {service.benefits.map((b) => (
                          <li key={b} className="flex items-start gap-3 text-navy">
                            <CheckCircle2 className="w-4 h-4 text-orange-accent flex-shrink-0 mt-1" />
                            <span className="text-sm leading-relaxed">{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="bg-navy text-white rounded-2xl p-7">
                      <h4 className="text-xs uppercase tracking-[0.2em] text-orange-accent font-bold mb-5">Ideal For</h4>
                      <ul className="space-y-3">
                        {service.idealFor.map((i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange-accent mt-2 flex-shrink-0" />
                            <span className="text-sm leading-relaxed text-white/90">{i}</span>
                          </li>
                        ))}
                      </ul>
                      <Link
                        to="/quote"
                        className="mt-7 inline-flex items-center gap-2 text-orange-accent text-sm font-semibold link-underline"
                      >
                        Get a quote for this service <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-grey-soft">
        <div className="max-w-4xl mx-auto text-center px-6">
          <SectionHeading
            centered
            eyebrow="Bundle & Save"
            title="Combine services. Unify costs."
            description="Most clients bundle 3+ service lines for significant savings and simplified reporting. Let's design the right mix for your portfolio."
          />
          <Link
            to="/quote"
            className="mt-10 inline-flex items-center gap-2 bg-orange-accent hover:bg-orange-dark text-white px-8 py-4 rounded-md font-semibold transition-all group shadow-lg shadow-orange-accent/30"
          >
            Build a Custom Plan <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  )
}

export default Services
